import React from 'react';
import styled from 'styled-components';

import {Notice} from '@famly/mf_data-display_notice';
import {Text} from '@famly/mf_data-display_text';
import {Stack} from '@famly/mf_layout_stack';
import {Box} from '@famly/mf_layout_box';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';

import type * as Types from '../types';
import {SummaryPeriod} from './summary-period';

type Props = {
    pricingGroups: Types.SummaryPricingGroups;
    summaryPeriods: Array<Types.SummaryPeriod>;
    summaryInfoCopy: string;
    pastSummaryPeriods: Array<Types.SummaryPeriod>;
    currency: string;
    showInvoiceSummary: boolean;
    canCalculateInvoiceAmount: boolean;
};

export const Summary = ({
    pricingGroups,
    summaryPeriods,
    showInvoiceSummary,
    pastSummaryPeriods,
    currency,
    summaryInfoCopy,
    canCalculateInvoiceAmount,
}: Props) => {
    const hasChildThatMovesPricingGroups = React.useMemo(
        () =>
            (showInvoiceSummary &&
                canCalculateInvoiceAmount &&
                summaryPeriods.some(period => period.hasChildThatMovesPricingGroups)) ||
            pastSummaryPeriods.some(period => period.hasChildThatMovesPricingGroups),
        [showInvoiceSummary, canCalculateInvoiceAmount, summaryPeriods, pastSummaryPeriods],
    );

    const headerElements = React.useMemo(() => {
        const shouldShowHeader =
            (showInvoiceSummary && canCalculateInvoiceAmount && summaryPeriods.length > 0) ||
            pastSummaryPeriods.length > 0;
        return shouldShowHeader ? (
            <tr>
                <th></th>
                {Object.entries(pricingGroups).map(([pricingGroupId, {name}]) => (
                    <TH
                        key={pricingGroupId}
                        data-e2e-id={`pricing-group-column-${pricingGroupId}`}
                        data-e2e-class="pricing-group-column"
                    >
                        <Text emphasized variant="body-small">
                            {name}
                        </Text>
                    </TH>
                ))}
            </tr>
        ) : null;
    }, [
        showInvoiceSummary,
        canCalculateInvoiceAmount,
        summaryPeriods.length,
        pastSummaryPeriods.length,
        pricingGroups,
    ]);

    const pastSummaryElements = React.useMemo(
        () =>
            pastSummaryPeriods.map((period, index) => (
                <SummaryPeriod
                    key={index}
                    index={index - pastSummaryPeriods.length}
                    {...period}
                    currency={currency}
                    isPastSummary={true}
                    needsSquigglyLine={index + 1 === pastSummaryPeriods.length}
                />
            )),
        [pastSummaryPeriods, currency],
    );

    const summaryElements = React.useMemo(() => {
        if (!showInvoiceSummary) {
            return null;
        }
        if (!canCalculateInvoiceAmount) {
            return (
                <Stack direction="column" justifyContent="space-between" mt={4}>
                    <Notice
                        text={needsTranslations(
                            'SimplifiedUsBilling',
                            'The amount due will be calculated when the invoice is created',
                        )}
                        data-e2e-class="summary-notice"
                    />
                </Stack>
            );
        }
        return summaryPeriods.map((period, index) => (
            <SummaryPeriod key={index} index={index} {...period} currency={currency} />
        ));
    }, [showInvoiceSummary, canCalculateInvoiceAmount, summaryPeriods, currency]);

    return (
        <>
            <Box mb={6}>
                <Text color="n300" variant="body-small" textAlign="right" data-e2e-id="pricing-group-change-notice">
                    {hasChildThatMovesPricingGroups
                        ? needsTranslations('SimplifiedUsBilling', '* Moves to new pricing group')
                        : null}
                </Text>
            </Box>
            <table data-e2e-class="summary-table">
                {headerElements}
                {pastSummaryElements}
                {summaryElements}
            </table>
            <Text color="n300" variant="body-small" textAlign="center" data-e2e-id="summary-info-copy">
                {summaryInfoCopy}
            </Text>
        </>
    );
};

const TH = styled.th`
    text-align: right;
`;
